import React, { useContext, useState } from 'react';
import axios from 'axios';
import { Context as SettingsContext } from 'components/setting/context';
import Select from "components/forms/select/index";
import Textarea from "components/forms/textarea";
import Button from "components/button/index";
import openNotificationWithIcon from 'utils/notification';
import * as css from './style';
import { AI_PROXY_PATHS, aiProxyHeaders, aiProxyUrl } from 'utils/aiProxy';
import { stripInlineMarkdown } from 'utils/aiText';

interface Props {
  handleUpdateKeywords: (keywords) => void ,
}

const KeywordSuggestionForm = ({ handleUpdateKeywords }: Props) => {
  const { settings } = useContext(SettingsContext);
  const chatGPTKey = settings['openai.api.key']?.stringValue;

  const [loader, setLoader] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);
  const [selectedTags, setTags] = useState([]);

  /**
   * Handle on change of prompt field
   */
  const handleChange = (event) => {
    setPromptText(event.target.value);
  };

  function onChangeField(event) {
    setTags(event.target.value)
  }

  /**
   * Manage openAI call for image creation based on selected content
   */
  const handleClick = async () => {
    if (!chatGPTKey) {
      openNotificationWithIcon({
        type: 'error',
        title: 'Please set system settings openai.api.key to access chatGPT',
      }, () => { });
      return;
    }
    if (!promptText.trim()) {
      openNotificationWithIcon({
        type: 'error',
        title: 'Please enter text to process with chatGPT',
      }, () => { });
      return;
    }
    if (promptText.length > 1000) {
      openNotificationWithIcon({
        type: 'error',
        title: 'Maximum 1000 characters are allowed in prompt.',
      }, () => { });
      return;
    }

    setLoader(true);
    const data = {
      model: 'gpt-5', // GPT-5 for high-quality keyword extraction
      // gpt-5 spends reasoning tokens out of this budget before it writes
      // anything, and at the default effort it can spend all of it and return
      // an empty reply. Both are ceilings, so an ordinary run bills the same.
      max_completion_tokens: 4096,
      reasoning_effort: 'low',
      messages: [
        {
          "role": "system",
          "content": "You will be provided with a block of text, and your task is to extract a list of comma separated keywords from it."
        },
        {
          "role": "user",
          "content": promptText
        }
      ]
    };

    //Request for a query from OpenAI, through the api's proxy
    const responseData = await axios.request({
      url: aiProxyUrl(AI_PROXY_PATHS.openaiChat),
      method: 'post',
      headers: aiProxyHeaders(),
      data: data
    })
      .catch((err) => {
        return err?.response?.data;
      });

    if (responseData?.data?.choices?.length > 0) {
      const keywords = (responseData?.data?.choices[0]?.message?.content || '')
        .split(',')
        .map((keyword) => stripInlineMarkdown(keyword))
        .filter(Boolean)
        .map((keyword) => ({ label: keyword, value: keyword }));

      // A run that hits the token ceiling answers with an empty body rather
      // than an error, which used to leave one blank option in the list.
      if (keywords.length === 0) {
        openNotificationWithIcon({
          type: 'error',
          title: 'No keywords came back. Please try again.',
        }, () => { });
        setLoader(false);
        return;
      }

      setSuggestedKeywords(keywords);

    } else if (responseData?.error) {
      openNotificationWithIcon({
        type: 'error',
        title: responseData?.error?.message || 'Server responds with error!',
      }, () => { });
    } else { }
    setLoader(false);
  };

  /**
   * Process selected keyword to set
   */
  const handleUpdateKeyword = () => {
    if (!selectedTags.length) {
      openNotificationWithIcon({
        type: 'error',
        title: 'Please select keywords.',
      }, () => { });
      return;
    }

    handleUpdateKeywords(selectedTags);
  };

  return (
    <css.KeywordSuggestWarpper>
      <css.Field>
        <Textarea
          theme="dark"
          ptext="Prompt"
          name="prompt"
          defaultValue={promptText}
          onChange={handleChange}
        />
      </css.Field>
      <Button
        icon="cloud-upload"
        color="chateauGreen"
        className='generate-button'
        loading={loader}
        disabled={loader}
        onClick={handleClick}
      >
        Generate Keyword
      </Button>
      {(suggestedKeywords.length > 0) &&
        <>
          <css.Field>
            <Select
              label="Suggested Keywords"
              placeholder="Keywords"
              mode="multiple"
              name="keywords"
              options={suggestedKeywords}
              defaultValue={selectedTags}
              onChange={onChangeField}
              maxWidth="100%"
            />
          </css.Field>
          <css.AddButtonWrapper>
            <Button
              color="pictonBlue"
              icon="plus-circle"
              onClick={handleUpdateKeyword}
            >
              Add
            </Button>
          </css.AddButtonWrapper>
        </>
      }
    </css.KeywordSuggestWarpper>
  );
};

export default KeywordSuggestionForm;